import { useCallback, useEffect, useRef, useState } from "react";
import {
  Award, Bell, CalendarPlus, Camera, Clock, Coffee, Download, FileText, IndianRupee, Loader2,
  LogIn, LogOut, MapPin, Mic, Navigation, ShieldCheck, TimerReset, User, Utensils, WifiOff,
} from "lucide-react";
import { api, apiError, fileUrl, inr, logError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BrandMark } from "@/context/BrandingContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import VoiceInput from "@/components/VoiceInput";
import { toast } from "sonner";

const QUEUE_KEY = "pramsia-attendance-queue";
const PING_QUEUE_KEY = "pramsia-ping-queue";

const readQueue = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
};
const writeQueue = (key, q) => localStorage.setItem(key, JSON.stringify(q));

function getLocation() {
  return new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: Math.round(p.coords.accuracy) }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
}

const TABS = [
  ["home", "Home", Clock],
  ["attendance", "Attendance", MapPin],
  ["score", "Score", Award],
  ["money", "Salary", IndianRupee],
  ["leave", "Leave", CalendarPlus],
  ["speak", "Speak", Mic],
  ["payslips", "Payslips", FileText],
  ["profile", "Profile", User],
];

const Stat = ({ label, value, testId }) => (
  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-3" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">{label}</div>
    <div className="mt-1 font-mono-num text-xl font-semibold text-white">{value}</div>
  </div>
);

export default function EmployeeApp() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("home");
  const [voice, setVoice] = useState({ text: "", audio_url: "", sending: false, result: null });
  const [busy, setBusy] = useState("");
  const [online, setOnline] = useState(navigator.onLine);
  const [queued, setQueued] = useState(readQueue(QUEUE_KEY).length);
  const [gps, setGps] = useState(null);
  const [geo, setGeo] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [installEvent, setInstallEvent] = useState(null);
  const [exception, setException] = useState("");
  const [leave, setLeave] = useState({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });
  const [score, setScore] = useState(null);
  const [graph, setGraph] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const pingTimer = useRef(null);

  const load = useCallback(() => {
    api.get("/hr/my/workspace").then(({ data: d }) => setData(d))
      .catch((e) => logError("employee workspace", e));
    api.get("/tracking/my/session").then(({ data: d }) => setTracking(d))
      .catch((e) => logError("tracking session", e));
    api.get("/performance/me").then(({ data: d }) => setScore(d))
      .catch((e) => logError("my scorecard", e));
    api.get("/performance/graph/me?metric=attendance&months=6").then(({ data: d }) => setGraph(d))
      .catch((e) => logError("my graph", e));
  }, []);

  const syncQueues = useCallback(async () => {
    if (!navigator.onLine) return;
    const attendance = readQueue(QUEUE_KEY);
    const leftAttendance = [];
    for (const item of attendance) {
      try {
        await api.post(`/hr/attendance/${item.kind}`, item.payload);
      } catch (e) {
        if (!String(apiError(e)).includes("already")) leftAttendance.push(item);
      }
    }
    writeQueue(QUEUE_KEY, leftAttendance);
    setQueued(leftAttendance.length);

    const pings = readQueue(PING_QUEUE_KEY);
    const leftPings = [];
    for (const item of pings) {
      try {
        await api.post("/tracking/ping", item);
      } catch {
        leftPings.push(item);
      }
    }
    writeQueue(PING_QUEUE_KEY, leftPings);
    if (attendance.length > leftAttendance.length || pings.length > leftPings.length) {
      toast.success("Pending records synced");
      load();
    }
  }, [load]);

  useEffect(() => {
    load();
    syncQueues();
    const on = () => { setOnline(true); syncQueues(); };
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const prompt = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", prompt);
    const poll = setInterval(load, 60000);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
      window.removeEventListener("beforeinstallprompt", prompt);
      clearInterval(poll);
    };
  }, [load, syncQueues]);

  const checkLocation = useCallback(async () => {
    setBusy("gps");
    const position = await getLocation();
    setGps(position);
    if (!position) {
      setGeo({ status: "no_location", message: "Location unavailable — turn on GPS for this app" });
      setBusy("");
      return null;
    }
    try {
      const { data: locations } = await api.get("/tracking/locations");
      const inside = locations
        .filter((l) => l.active)
        .map((l) => {
          const R = 6371000;
          const toRad = (v) => (v * Math.PI) / 180;
          const dLat = toRad(l.lat - position.lat);
          const dLng = toRad(l.lng - position.lng);
          const a = Math.sin(dLat / 2) ** 2 +
            Math.cos(toRad(position.lat)) * Math.cos(toRad(l.lat)) * Math.sin(dLng / 2) ** 2;
          return { ...l, distance: Math.round(R * 2 * Math.asin(Math.sqrt(a))) };
        })
        .sort((a, b) => a.distance - b.distance)[0];
      if (!inside) setGeo({ status: "no_geofence", message: "No approved work location is set up yet" });
      else if (inside.distance <= inside.radius_m)
        setGeo({ status: "verified", message: `Location verified — ${inside.name}`, ...inside });
      else
        setGeo({
          status: "outside",
          message: `You are ${inside.distance} m away from ${inside.name} (allowed ${inside.radius_m} m)`,
          ...inside,
        });
    } catch (e) {
      setGeo({ status: "unknown", message: apiError(e) });
    }
    setBusy("");
    return position;
  }, []);

  // Foreground live tracking: the app pings while the session is active and the app is open.
  useEffect(() => {
    clearInterval(pingTimer.current);
    if (tracking?.tracking !== "active" || !tracking?.session?.id) return undefined;
    const interval = Math.max(30, Number(tracking.interval_seconds || 300)) * 1000;
    const sendPing = async () => {
      const position = await getLocation();
      if (!position) return;
      const payload = {
        session_id: tracking.session.id,
        ...position,
        recorded_at: new Date().toISOString(),
        client_ref: `${tracking.session.id}-${Date.now()}`,
        offline: !navigator.onLine,
      };
      if (!navigator.onLine) {
        writeQueue(PING_QUEUE_KEY, [...readQueue(PING_QUEUE_KEY), payload]);
        return;
      }
      try {
        const { data: res } = await api.post("/tracking/ping", payload);
        setGeo(res.geofence || null);
        setGps(position);
        setTracking((t) => (t ? { ...t, freshness: "live", label: "LIVE" } : t));
      } catch {
        writeQueue(PING_QUEUE_KEY, [...readQueue(PING_QUEUE_KEY), payload]);
      }
    };
    sendPing();
    pingTimer.current = setInterval(sendPing, interval);
    return () => clearInterval(pingTimer.current);
  }, [tracking?.tracking, tracking?.session?.id, tracking?.interval_seconds]);

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch {
      toast.error("Camera not available — attendance will be marked without a selfie");
    }
  };

  const grabSelfie = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return "";
    const canvas = document.createElement("canvas");
    canvas.width = 480;
    canvas.height = Math.round((video.videoHeight / video.videoWidth) * 480);
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL("image/jpeg", 0.7).split(",")[1];
  };

  const mark = async (kind) => {
    setBusy(kind);
    try {
      const position = gps || (await getLocation());
      const payload = {
        ...(position || {}),
        client_ref: `${kind}-${new Date().toISOString().slice(0, 10)}-${Date.now()}`,
        device: /Mobi|Android/i.test(navigator.userAgent) ? "mobile" : "desktop",
      };
      if (kind === "check-in") {
        payload.selfie_base64 = grabSelfie();
        if (exception) payload.exception_reason = exception;
      }
      if (!navigator.onLine) {
        const queue = [...readQueue(QUEUE_KEY),
          { kind, payload: { ...payload, offline_at: new Date().toISOString() } }];
        writeQueue(QUEUE_KEY, queue);
        setQueued(queue.length);
        toast.success("Saved offline — it will sync when you are back online");
      } else {
        const { data: res } = await api.post(`/hr/attendance/${kind}`, payload);
        if (kind === "check-in") {
          setGeo(res.geofence || null);
          toast.success(
            res.geofence?.status === "verified"
              ? "Checked in — location verified"
              : "Checked in — sent for HR review (outside approved location)"
          );
        } else {
          toast.success(`Checked out — ${res.worked_hours}h recorded`);
        }
        setException("");
        load();
      }
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const punch = async (kind) => {
    setBusy(kind);
    try {
      const position = await getLocation();
      await api.post("/hr/attendance/punch", { kind, ...(position || {}) });
      const labels = {
        lunch_out: "Lunch started", lunch_in: "Welcome back from lunch",
        break_start: "Break started", break_end: "Break ended",
      };
      toast.success(labels[kind]);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  const applyLeave = async () => {
    try {
      await api.post("/hr/leave", leave);
      toast.success("Leave request sent");
      setLeave({ leave_type: "Casual Leave", from_date: "", to_date: "", reason: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const install = async () => {
    if (!installEvent) {
      toast.info("On Android Chrome use the ⋮ menu → “Add to Home screen”. On iPhone use Share → Add to Home Screen.");
      return;
    }
    installEvent.prompt();
    const { outcome } = await installEvent.userChoice;
    if (outcome === "accepted") toast.success("Employee app installed");
    setInstallEvent(null);
  };

  if (!data) {
    return (
      <div className="flex h-screen items-center justify-center bg-[#0d2f24]">
        <Loader2 className="h-6 w-6 animate-spin text-white/70" />
      </div>
    );
  }

  const today = data.today;
  const trackingState = tracking?.tracking || "off";
  const trackingLabel = !online
    ? "OFFLINE"
    : trackingState === "active"
      ? tracking?.label === "LIVE" ? "TRACKING ACTIVE" : tracking?.label || "TRACKING ACTIVE"
      : trackingState === "paused" ? "TRACKING PAUSED" : "TRACKING OFF";

  return (
    <div className="min-h-screen bg-[#0d2f24] pb-24 text-white" data-testid="employee-app">
      <header className="sticky top-0 z-20 border-b border-white/10 bg-[#0d2f24]/95 px-4 py-3 backdrop-blur">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <BrandMark className="h-8 w-8" />
            <div>
              <div className="text-sm font-semibold" data-testid="employee-name">
                {data.me.name}
              </div>
              <div className="text-[11px] text-white/60">
                {data.me.employee_code} • {data.me.designation || data.me.role}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {online ? (
              <Badge className="bg-emerald-500/20 text-[10px] text-emerald-200" data-testid="employee-online-badge">
                online
              </Badge>
            ) : (
              <Badge variant="destructive" className="gap-1 text-[10px]" data-testid="employee-offline-badge">
                <WifiOff className="h-3 w-3" /> offline
              </Badge>
            )}
            {queued > 0 && (
              <Badge className="bg-amber-500/20 text-[10px] text-amber-200" data-testid="employee-queue-badge">
                {queued} pending sync
              </Badge>
            )}
            <Button size="sm" variant="ghost" className="text-white/70" onClick={logout}
              data-testid="employee-logout">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="space-y-4 px-4 py-4">
        {tab === "home" && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <Stat label="Present days" value={data.present_days} testId="employee-stat-present" />
              <Stat label="Overtime hours" value={data.ot_hours} testId="employee-stat-ot" />
              <Stat label="Shift" value={`${data.shift.start}–${data.shift.end}`} testId="employee-stat-shift" />
              <Stat
                label="Advance balance"
                value={data.salary_visible
                  ? inr(data.advances.reduce((s, a) => s + Number(a.balance || 0), 0))
                  : "—"}
                testId="employee-stat-advance"
              />
            </div>

            {trackingState !== "off" && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="employee-tracking-card">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Navigation className="h-4 w-4 text-amber-300" /> {trackingLabel}
                  </div>
                  {trackingState === "active" && (
                    <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                  )}
                </div>
                <p className="mt-1 text-xs text-white/60" data-testid="employee-tracking-detail">
                  {`Authorised by ${tracking?.session?.authorised_by} • every ${Math.round(
                    (tracking?.interval_seconds || 300) / 60
                  )} min • ${tracking?.session?.ping_count || 0} updates sent`}
                </p>
                <p className="mt-1 text-[11px] text-amber-200/80">
                  Keep this app open during field duty — a phone browser cannot send location
                  reliably in the background.
                </p>
              </div>
            )}

            <Button onClick={install} variant="secondary" className="w-full gap-2"
              data-testid="employee-install-button">
              <Download className="h-4 w-4" /> Install employee app
            </Button>

            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="employee-notifications">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Bell className="h-4 w-4 text-amber-300" /> Notifications
              </div>
              {data.announcements.length === 0 ? (
                <p className="mt-2 text-xs text-white/50">Nothing new.</p>
              ) : (
                data.announcements.slice(0, 6).map((n) => (
                  <div key={n.id} className="mt-2 border-t border-white/10 pt-2 text-xs">
                    <div className="font-medium">{n.title}</div>
                    <div className="text-white/60">{n.body}</div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {tab === "attendance" && (
          <>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="employee-attendance-card">
              {today ? (
                <div className="text-sm">
                  Checked in at <b>{String(today.check_in).slice(11, 16)}</b>
                  {today.check_out
                    ? <> • out at <b>{String(today.check_out).slice(11, 16)}</b> • {today.worked_hours}h</>
                    : " • not checked out yet"}
                  <div className="mt-1 text-xs text-white/60">
                    face {today.face_status} • {today.geofence?.status === "verified"
                      ? `location verified (${today.geofence.location_name})`
                      : today.geofence?.message || (today.gps ? "location recorded" : "no location")}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-white/70">You have not marked attendance today.</p>
              )}

              <div className="mt-3 flex items-center gap-2">
                <video ref={videoRef} muted playsInline
                  className="h-24 w-32 rounded-xl bg-black/40 object-cover"
                  data-testid="employee-selfie-preview" />
                <div className="space-y-2">
                  <Button size="sm" variant="secondary" className="gap-1.5" onClick={startCamera}
                    data-testid="employee-camera-button">
                    <Camera className="h-3.5 w-3.5" /> Open camera
                  </Button>
                  <Button size="sm" variant="secondary" className="gap-1.5" onClick={checkLocation}
                    disabled={busy === "gps"} data-testid="employee-gps-button">
                    {busy === "gps" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <MapPin className="h-3.5 w-3.5" />}
                    Check my location
                  </Button>
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-white/10 p-3 text-xs" data-testid="employee-gps-status">
                {gps ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className={`h-3.5 w-3.5 ${geo?.status === "verified" ? "text-emerald-300" : "text-amber-300"}`} />
                      <span className="font-medium">
                        {geo?.status === "verified" ? "LOCATION VERIFIED" : geo?.status === "outside" ? "OUTSIDE APPROVED LOCATION" : "LOCATION RECORDED"}
                      </span>
                    </div>
                    <div className="mt-1 text-white/60">
                      {geo?.message} • accuracy ±{gps.accuracy} m
                    </div>
                  </>
                ) : (
                  <span className="text-white/60">
                    GPS status: not checked yet. Tap “Check my location”.
                  </span>
                )}
              </div>

              {geo?.status === "outside" && !today && (
                <div className="mt-3 space-y-2" data-testid="employee-exception-box">
                  <Input
                    placeholder="Why are you outside? (attendance exception request)"
                    value={exception}
                    onChange={(e) => setException(e.target.value)}
                    className="bg-white/5 text-white placeholder:text-white/40"
                    data-testid="employee-exception-input"
                  />
                  <p className="text-[11px] text-amber-200/80">
                    This will be recorded for HR review — it is not counted as verified attendance.
                  </p>
                </div>
              )}

              <div className="mt-3 flex gap-2">
                {!today && (
                  <Button className="flex-1 gap-1.5" onClick={() => mark("check-in")}
                    disabled={busy === "check-in"} data-testid="employee-checkin-button">
                    {busy === "check-in" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                    Check in
                  </Button>
                )}
                {today && !today.check_out && (
                  <Button className="flex-1 gap-1.5" onClick={() => mark("check-out")}
                    disabled={busy === "check-out"} data-testid="employee-checkout-button">
                    {busy === "check-out" ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogOut className="h-4 w-4" />}
                    Check out
                  </Button>
                )}
              </div>

              {today && !today.check_out && (
                <div className="mt-3 space-y-2 border-t border-white/10 pt-3" data-testid="employee-punch-box">
                  <div className="overline text-white/50">Lunch & break punches</div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button size="sm" variant="secondary" className="gap-1.5"
                      disabled={!!busy || !!today.lunch_out}
                      onClick={() => punch("lunch_out")} data-testid="employee-lunch-out">
                      <Utensils className="h-3.5 w-3.5" />
                      {today.lunch_out ? `Lunch out ${String(today.lunch_out).slice(11, 16)}` : "Lunch out"}
                    </Button>
                    <Button size="sm" variant="secondary" className="gap-1.5"
                      disabled={!!busy || !today.lunch_out || !!today.lunch_in}
                      onClick={() => punch("lunch_in")} data-testid="employee-lunch-in">
                      <Utensils className="h-3.5 w-3.5" />
                      {today.lunch_in ? `Lunch in ${String(today.lunch_in).slice(11, 16)}` : "Lunch in"}
                    </Button>
                    {(() => {
                      const open = (today.breaks || []).find((b) => !b.end);
                      return open ? (
                        <Button size="sm" variant="secondary" className="col-span-2 gap-1.5"
                          disabled={!!busy} onClick={() => punch("break_end")}
                          data-testid="employee-break-end">
                          <Coffee className="h-3.5 w-3.5" /> End break (started{" "}
                          {String(open.start).slice(11, 16)})
                        </Button>
                      ) : (
                        <Button size="sm" variant="secondary" className="col-span-2 gap-1.5"
                          disabled={!!busy} onClick={() => punch("break_start")}
                          data-testid="employee-break-start">
                          <Coffee className="h-3.5 w-3.5" /> Start break
                          {(today.breaks || []).length ? ` (${today.breaks.length} today)` : ""}
                        </Button>
                      );
                    })()}
                  </div>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
              data-testid="employee-attendance-history">
              <div className="overline text-white/50">This month</div>
              {data.attendance.map((a) => (
                <div key={a.id} className="flex items-center justify-between border-t border-white/10 py-2 text-xs">
                  <span>{a.date} • {a.status}</span>
                  <span className="text-white/60">
                    {String(a.check_in || "").slice(11, 16)}–{String(a.check_out || "").slice(11, 16) || "…"}
                    {a.ot_hours ? ` • OT ${a.ot_hours}h` : ""}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "score" && (
          <div className="space-y-3" data-testid="employee-score-tab">
            {score ? (
              <>
                <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                    Overall performance ({score.period})
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <span className="font-mono-num text-3xl font-semibold"
                      data-testid="employee-overall-score">
                      {score.overall.display}
                    </span>
                    <span className="rounded-full px-2 py-0.5 text-[11px]"
                      style={{ background: `${score.overall.colour}22`, color: score.overall.colour }}
                      data-testid="employee-overall-zone">
                      {score.overall.zone_label}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-white/60">
                    {score.facts.present_days}/{score.facts.applicable_days} days present • worked{" "}
                    {score.facts.worked_hours}h of {score.facts.required_hours}h • OT{" "}
                    {score.facts.ot_hours}h
                  </p>
                </div>

                <div className="space-y-2 rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                  data-testid="employee-score-list">
                  {Object.entries(score.metrics).map(([key, m]) => (
                    <div key={key} className="border-t border-white/10 pt-2 first:border-0 first:pt-0"
                      data-testid={`employee-score-${key}`}>
                      <div className="flex items-center justify-between text-xs">
                        <span>{m.label}</span>
                        <span className="font-mono-num" style={{ color: m.no_data ? "#94a3b8" : m.colour }}>
                          {m.no_data ? "no data" : m.display}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-white/10">
                        <div className="h-full rounded-full transition-all"
                          style={{ width: `${m.percent}%`, background: m.no_data ? "#475569" : m.colour }} />
                      </div>
                    </div>
                  ))}
                </div>

                {score.improvement_areas.length > 0 && (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                    data-testid="employee-improvements">
                    <div className="text-sm font-medium">Where you can improve</div>
                    {score.improvement_areas.map((a) => (
                      <div key={a.metric} className="mt-1 text-xs text-white/60">
                        • {a.label} — {a.percent}%
                      </div>
                    ))}
                  </div>
                )}

                {graph && (
                  <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
                    data-testid="employee-graph">
                    <div className="text-sm font-medium">Last 6 months — attendance</div>
                    <div className="mt-3 flex h-28 items-end gap-2">
                      {graph.points.map((p) => (
                        <div key={p.month} className="flex flex-1 flex-col items-center gap-1">
                          <div className="w-full rounded-t bg-amber-300/80"
                            style={{ height: `${Math.max(3, p.percent)}%` }}
                            title={`${p.month}: ${p.percent}%`} />
                          <span className="text-[9px] text-white/50">{p.month.slice(5)}</span>
                        </div>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] text-white/50">
                      Overall this month: {score.overall.percent}% • time loss{" "}
                      {score.facts.time_loss_hours}h
                    </p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-white/60">Your scorecard will appear once attendance is recorded.</p>
            )}
          </div>
        )}

        {tab === "money" && (
          <div className="space-y-3">
            {data.salary_visible ? (
              <div className="grid grid-cols-2 gap-3">
                <Stat label="Overtime hours (month)" value={data.ot_hours} testId="employee-money-ot" />
                <Stat
                  label="Last net salary"
                  value={inr(data.payslips[0]?.net_pay || 0)}
                  testId="employee-money-net"
                />
              </div>
            ) : (
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm"
                data-testid="employee-salary-hidden">
                Salary details are not visible for your account. Please contact HR.
              </div>
            )}
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4"
              data-testid="employee-hours-card">
              <div className="text-sm font-medium">Working hours this month</div>
              <div className="mt-1 text-xs text-white/60">
                Worked {data.worked_hours}h • overtime {data.ot_hours}h
              </div>
              <div className="mt-2 text-sm font-medium">Time loss</div>
              <div className="mt-1 text-xs text-white/60" data-testid="employee-time-loss">
                {data.time_loss.total_hours}h — late {data.time_loss.late_minutes}m, early out{" "}
                {data.time_loss.early_minutes}m, lunch {data.time_loss.lunch_late_minutes}m, break{" "}
                {data.time_loss.break_excess_minutes}m
              </div>
              <p className="mt-1 text-[11px] text-amber-200/80">
                Status: {data.time_loss.status}. Nothing is deducted unless HR approves it.
              </p>
            </div>
            {data.salary_visible && (
              <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="employee-advances">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <TimerReset className="h-4 w-4 text-amber-300" /> Advances / loans
                </div>
                {data.advances.length === 0 ? (
                  <p className="mt-2 text-xs text-white/50">No running advance.</p>
                ) : (
                  data.advances.map((a) => (
                    <div key={a.id} className="mt-2 flex justify-between border-t border-white/10 pt-2 text-xs">
                      <span>{a.reason || "Advance"} • {a.month || ""}</span>
                      <span className="font-mono-num">{inr(a.balance)}</span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {tab === "leave" && (
          <div className="space-y-3">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="employee-leave-form">
              <Select value={leave.leave_type} onValueChange={(v) => setLeave({ ...leave, leave_type: v })}>
                <SelectTrigger className="bg-white/5 text-white" data-testid="employee-leave-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["Casual Leave", "Sick Leave", "Earned Leave", "Paid Leave", "Unpaid Leave"].map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <Input type="date" className="bg-white/5 text-white" data-testid="employee-leave-from"
                  value={leave.from_date} onChange={(e) => setLeave({ ...leave, from_date: e.target.value })} />
                <Input type="date" className="bg-white/5 text-white" data-testid="employee-leave-to"
                  value={leave.to_date} onChange={(e) => setLeave({ ...leave, to_date: e.target.value })} />
              </div>
              <Input placeholder="Reason" className="mt-2 bg-white/5 text-white"
                data-testid="employee-leave-reason" value={leave.reason}
                onChange={(e) => setLeave({ ...leave, reason: e.target.value })} />
              <Button className="mt-2 w-full" onClick={applyLeave}
                disabled={!leave.from_date || !leave.to_date} data-testid="employee-leave-submit">
                Apply for leave
              </Button>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="employee-leave-list">
              {data.leaves.length === 0 ? (
                <p className="text-xs text-white/50">No leave requests yet.</p>
              ) : (
                data.leaves.map((l) => (
                  <div key={l.id} className="flex items-center justify-between border-t border-white/10 py-2 text-xs first:border-0">
                    <span>{l.leave_type} • {l.from_date} → {l.to_date}</span>
                    <Badge variant={l.status === "Rejected" ? "destructive" : "outline"} className="text-[10px]">
                      {l.status}
                    </Badge>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {tab === "speak" && (
          <div className="space-y-3" data-testid="employee-speak-tab">
            <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/50">
                Speak instead of typing
              </div>
              <p className="mt-2 text-sm text-white/70">
                Tap Speak and say it in Hindi or English — for example
                “aaj 60 kg resin use hua” or “2 drum cobalt urgently chahiye”.
                Your manager gets it as a request.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <VoiceInput dark purpose="employee_report" label="Speak"
                  onTranscript={(text, meta) =>
                    setVoice({ text, audio_url: meta.audio_url, sending: false, result: null })} />
                {voice.audio_url && (
                  <audio controls src={fileUrl(voice.audio_url)} className="h-8 max-w-[180px]"
                    data-testid="employee-voice-playback" />
                )}
              </div>
              {voice.text && (
                <>
                  <Input className="mt-3 bg-white/5 text-white" data-testid="employee-voice-text"
                    value={voice.text}
                    onChange={(e) => setVoice({ ...voice, text: e.target.value })} />
                  <p className="mt-1 text-[11px] text-white/50">
                    Check the words and correct them if needed before sending.
                  </p>
                  <Button className="mt-2 w-full" disabled={voice.sending}
                    data-testid="employee-voice-send"
                    onClick={async () => {
                      setVoice((v) => ({ ...v, sending: true }));
                      try {
                        const { data: r } = await api.post("/ai/employee-message", {
                          text: voice.text,
                          audio_file_id: (voice.audio_url || "").split("/").pop() || "",
                          source: "app",
                        });
                        setVoice({ text: "", audio_url: "", sending: false, result: r });
                        toast.success("Sent to your manager");
                      } catch (e) {
                        setVoice((v) => ({ ...v, sending: false }));
                        toast.error(apiError(e));
                      }
                    }}>
                    {voice.sending && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
                    Send to my manager
                  </Button>
                </>
              )}
              {voice.result && (
                <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3 text-xs"
                  data-testid="employee-voice-result">
                  <div className="text-white/80">
                    Understood as <span className="font-medium">{voice.result.intent}</span>
                  </div>
                  {voice.result.summary && (
                    <div className="mt-1 text-white/60">{voice.result.summary}</div>
                  )}
                  {voice.result.clarification_question && (
                    <div className="mt-1 text-amber-200/80">
                      {voice.result.clarification_question}
                    </div>
                  )}
                  <div className="mt-1 text-white/50">
                    Your manager will approve or ask you about it.
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "payslips" && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4" data-testid="employee-payslips">
            {data.payslips.length === 0 ? (
              <p className="text-xs text-white/50">No payslips yet.</p>
            ) : (
              data.payslips.map((p) => (
                <div key={p.id} className="flex items-center justify-between border-t border-white/10 py-2 text-xs first:border-0">
                  <span>{p.month}</span>
                  {p.salary_hidden ? (
                    <span className="text-white/50" data-testid={`employee-payslip-hidden-${p.id}`}>
                      hidden — contact HR
                    </span>
                  ) : (
                    <span className="flex items-center gap-3">
                      <span className="font-mono-num">{inr(p.net_pay)}</span>
                      <a href={`${process.env.REACT_APP_BACKEND_URL}/api/payroll/payslips/${p.id}/pdf`}
                        target="_blank" rel="noreferrer" className="underline"
                        data-testid={`employee-payslip-pdf-${p.id}`}>
                        PDF
                      </a>
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {tab === "profile" && (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-4 text-sm"
            data-testid="employee-profile">
            {[
              ["Name", data.me.name],
              ["Employee code", data.me.employee_code],
              ["Role", user?.role_label || data.me.role],
              ["Department", data.me.department || "—"],
              ["Designation", data.me.designation || "—"],
              ["Shift", `${data.shift.name} • ${data.shift.start}–${data.shift.end}`],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between border-t border-white/10 py-2 first:border-0">
                <span className="text-white/60">{k}</span>
                <span>{v}</span>
              </div>
            ))}
            <p className="mt-3 text-[11px] text-white/50">
              You can only see your own attendance, salary and location data. Nothing about other
              employees is available in this app.
            </p>
          </div>
        )}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-30 grid grid-cols-7 border-t border-white/10 bg-[#0b271e]/95 backdrop-blur"
        data-testid="employee-nav">
        {TABS.map(([key, label, Icon]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            data-testid={`employee-tab-${key}`}
            className={`flex flex-col items-center gap-1 py-2.5 text-[10px] transition-colors ${
              tab === key ? "text-amber-300" : "text-white/55"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
}
